import { apiAxios } from '@/service/axios.ts'
import { defineStore } from 'pinia'
import { useCookies } from 'vue3-cookies'
import { useLocalStorage } from '@vueuse/core'
import { AuthApi, IGroup, ISystemStep, IUser } from '@/service/api'
import router from '@/router'
import { PermissionEnum } from '@/components/authorization/model/Permissions.ts'
import { useGlobalStateStore } from '@/components/commons/model/GlobalState.ts'
import { i18n } from '@/main.ts'


interface MenuItemData {
  label: string
  icon?: string
  to?: string
  permission?: PermissionEnum
  items: MenuItemData[]
}


export const useAuthorizationStore = defineStore('authorizationStore', {
  state: () => ({
    authorizationData: useLocalStorage('authorizationData', {} as IUser),
    groupsForPaintShop: [] as IGroup[],
    users: [] as IUser[]
  }),
  getters: {
    getLoggedUser (state): IUser {
      return state.authorizationData
    },
    getLoggedUserId (state): string {
      return state.authorizationData.login
    },
    authorizationDataState (state): IUser {
      return state.authorizationData
    },
    getPaintShopsForLoggedUser (state): string[] {
      if (state.authorizationData.groups != null) {
        return state.authorizationData.groups.map(g => g.paintShops).flat() as string[]
      } else {
        return []
      }
    },
    getUserLocale (state): string {
      return state.authorizationData.language as string
    }
  },

  actions: {
    getGroupsForStep (step: ISystemStep): IGroup[] {
      return this.groupsForPaintShop.filter(group => group.allPermissions?.includes(step)) as IGroup[]
    },
    getUsersForStep (step: ISystemStep): IUser[] {
      return this.users.filter(user => user.groups?.some(group => this.groupsForPaintShop.find(groupWithPermissions => groupWithPermissions.id === group.id)?.allPermissions?.includes(step)))
    },
    getUsers (): IUser[] {
      return this.users as IUser[]
    },
    hasPermission (permission: PermissionEnum): boolean {
      if (this.authorizationData.allPermissions != null) {
        return this.authorizationData.allPermissions.includes(permission)
      } else {
        return false
      }
    },
    hasPermissionForMenuItem (menuItem: MenuItemData): boolean {
      // collect permissions from menuItem and its children
      // if any of them is true, return true
      if (menuItem.permission) {
        if (this.hasPermission(menuItem.permission)) {
          return true
        }
      }
      if (menuItem.items) {
        for (const child of menuItem.items) {
          if (this.hasPermissionForMenuItem(child)) {
            return true
          }
        }
      }
      return false
    },
    init (): void {

    },
    async login (username: string, password: string, isMobile: boolean = false): Promise<string> {
      apiAxios.defaults.headers.common.Authorization = ''
      return await new AuthApi().login({ login: username, password }).then((response) => {
        this.authorizationData = (response.data.user)
        useGlobalStateStore().init()
        if (response.data.user.defaultPaintShop !== null) {
          useGlobalStateStore().selectedPaintShopId = response.data.user.defaultPaintShop!.id
        } else {
          useGlobalStateStore().selectedPaintShopId = this.getPaintShopsForLoggedUser[0]
        }
        useGlobalStateStore().backendVersion = response.data.backendVersion
        useCookies().cookies.set('token', response.data.token, '12h')
        this.setLocale(response.data.user)
        if (response.data.user.mobile || isMobile) {
          return '/mobile/workLoad'
        } else {
          return '/'
        }
      })
    },
    logout () {
      this.authorizationData = {} as IUser
      useGlobalStateStore().clearLocalStorage()
      useCookies().cookies.remove('token')
      router.push('/auth/login').then()
    },
    async loadAllUsersWithGroups () {
      return await new AuthApi().getAllUsersWithGroups().then((response) => {
        this.users = response.data
        return response.data
      })
    },
    async loadAllGroupsForPaintShop () {
      await new AuthApi().getGroupsForShop(useGlobalStateStore().selectedPaintShopId)
        .then((response) => {
          this.groupsForPaintShop = response.data
        })
    },
    async updateUserDefaultPrinter (user: IUser) {
      return await new AuthApi().updateUserDefaultPrinter(user.login, user.defaultPrinter!.printerId!)
        .then()
    },
    async updateUserLanguage (user: IUser) {
      this.setLocale(user)
      return await new AuthApi().updateLanguage(user.login, user.language!)
        .then()
    },
    setLocale (user: IUser) {
      const supportedLocales = ['pl', 'en'] as const
      const locale = user.language && supportedLocales.includes(user.language as typeof supportedLocales[number])
        ? user.language as 'pl' | 'en'
        : 'pl' // Default to Polish if language is undefined or unsupported
      i18n.global.locale.value = locale
    }
  }
})



